import LayerGroup from 'ol/layer/Group';
import VectorLayer from 'ol/layer/Vector';
import VectorImageLayer from 'ol/layer/VectorImage';
import { unByKey } from 'ol/Observable';

import { FeatureProperty } from 'cadenza/map/feature-property';
import {
  VECTOR_LAYER_SOURCE_FEATURES_LOADED,
  VectorLayerSource
} from 'cadenza/map/vector-layer/vector-layer-source';
import { LayerProperty } from 'cadenza/map/layer-property';
import { getSelectedFeatureStyle } from 'cadenza/map/vector-layer/selected-feature-style';

/**
 * The buffer in pixels around the map viewport extent used by the renderer when getting features from the vector source.
 * So when the user pans or zooms out the map just a little bit, feature reload is not required.
 *
 * @type {number}
 */
const VECTOR_LAYER_RENDER_BUFFER = 100;

const GEOMETRY_VERTEX_COUNT_THRESHOLD = 10000; // For now it is just a best guess number. We could do more measurements to find a better threshold.

const LABEL_COUNT_THRESHOLD = 500;

/**
 * At the moment, client-side rendering of labels requires huge effort.
 * When the limit is crossed, then all further labels are skipped / not rendered
 * and a warning is added to SourceProperty.MESSAGES.
 *
 * @type {number}
 */
const LABEL_COUNT_LIMIT = 2000;

/**
 * Creates a vector feature layer, which gets the styled features from the server and renders them in the browser
 *
 * @param {ol.Map} map - open layers map object
 * @param {string} layerId - feature layer id
 * @param {object} [options] - Options
 * @param {'BOUNDING_BOX'|'ALL'} options.loadingStrategy - loading strategy, see ol/loadingstrategy.
 * @param {number} options.opacity - Allows to make the complete layer rendered semi transparent
 * @param {boolean} options.liveLayer - flag determined on the server side, if true, the features of the layer can be updated using special PUT request to the server side. The features themselves are rendered on the client side.
 * @param {string} options.projection - Projection
 * @param {boolean} options.areTimelinePointsConnectedWithLines - Whether the layer has timelines and its points should be connected with lines
 * @param {boolean} options.timelineTimestampAttributeName - For timeline data, what is the name of the date attribute
 * @param {string} options.url - Url for feature request
 * @param {string} options.labelAttributeNames - The names of the label attributes
 * @return {ol.layer.VectorLayer} a new vector layer
 */
export function createVectorLayer (map, layerId, {
  url,
  loadingStrategy,
  opacity,
  liveLayer = false,
  projection,
  areTimelinePointsConnectedWithLines,
  timelineTimestampAttributeName,
  labelAttributeNames
} = {}) {
  return new VectorLayerWrapper(map, layerId, {
    url,
    loadingStrategy,
    opacity,
    liveLayer,
    projection,
    areTimelinePointsConnectedWithLines,
    timelineTimestampAttributeName,
    labelAttributeNames
  });
}

export function isVectorLayer (layer) {
  return layer instanceof VectorLayerWrapper;
}

class VectorLayerWrapper extends LayerGroup {

  _vectorLayerSource;

  constructor (map, layerId, {
    url,
    loadingStrategy,
    opacity,
    liveLayer = false,
    projection,
    areTimelinePointsConnectedWithLines,
    timelineTimestampAttributeName,
    labelAttributeNames
  } = {}) {
    const vectorLayerSource = new VectorLayerSource(map, layerId, {
      url,
      loadingStrategy,
      projection,
      layerRenderBuffer: VECTOR_LAYER_RENDER_BUFFER,
      areTimelinePointsConnectedWithLines,
      timelineTimestampAttributeName,
      labelAttributeNames,
      labelCountLimit: LABEL_COUNT_LIMIT
    });
    const layerOptions = {
      source: vectorLayerSource,
      opacity,
      renderBuffer: VECTOR_LAYER_RENDER_BUFFER
    };

    // One source and 2 layers using it, that seems to work fine.
    // We create both VectorLayer and VectorImageLayer and initially set VectorLayer to be active.
    // Each time the features are loaded, we check if they can be rendered with VectorLayer, if not, we swith dynamically to VectorImageLayer.
    // VectorLayer offers nice smooth rendering, the features are rendered during for example zoom animation. It may slow down the browser for large feature count or complex geometries.
    // VectorImageLayer renders the features once to an image and then reuses this image during map view animations. So it is not redrawn during zoom, but only after zooming is finished.
    const vectorLayer = new VectorLayer(layerOptions);
    const vectorImageLayer = new VectorImageLayer({ ...layerOptions, visible: false });
    super({
      layers: [ vectorLayer, vectorImageLayer ],
      properties: { liveLayer }
    });

    this._vectorLayerSource = vectorLayerSource;

    this.on(`change:${LayerProperty.SELECTED_FEATURE_IDS}`, () => this.updateSelectedFeatureStyles());
    vectorLayerSource.on(VECTOR_LAYER_SOURCE_FEATURES_LOADED, () => this.updateSelectedFeatureStyles());

    const olEvent = vectorLayerSource.on(VECTOR_LAYER_SOURCE_FEATURES_LOADED, () => {
      const switchToImage = shouldBeRenderedAsImageLayer(vectorLayerSource.getFeatures());
      if (switchToImage) {
        unByKey(olEvent);
        vectorLayer.setVisible(false);
        vectorImageLayer.setVisible(true);
        vectorLayerSource.changed();
      }
    });
  }

  getSource () {
    return this._vectorLayerSource;
  }

  // TODO_LATER (solovjova, 12.05.2020, CADENZA-15086 Routing-VectorLayer): selection (see https://conf.disy.net/x/eYepG)
  // This is a minimal working solution to enable feature selection visualization for the vector
  // layers rendered on the client side. Things to improve in the future:
  // - Selected feature styling.
  // - FeatureIds are not unique for the group themes. Layer ids should be included additionally to each featureId.
  // - Logic simplification.
  //    Now the selection relies on the same selection mechanism used for the WMS layers.
  //    The selection request with a clicked point or a drawn geometry is send to the server,
  //    the server finds the hit features, updates the selection state and return selected features to the client.
  //    For the vector layers it could be simplified, the features are available on the client,
  //    so there is no need to do the server request. Only do the request to update the state on the server.
  updateSelectedFeatureStyles () {
    const featureIds = this.get(LayerProperty.SELECTED_FEATURE_IDS);
    this.getSource().getFeatures()
      .forEach(feature => {
        const isSelected = featureIds.includes(String(feature.getId()));
        if (isSelected && !feature.originalStyle) {
          feature.originalStyle = feature.getStyle();
          feature.setStyle(getSelectedFeatureStyle(feature));
        } else if (!isSelected && feature.originalStyle) {
          feature.setStyle(feature.originalStyle);
          feature.originalStyle = undefined;
        }
      });
  }

}

function shouldBeRenderedAsImageLayer (features) {
  const totalVertexCount = features.flatMap(feature => coordinateCount(feature.getGeometry())).reduce((sum, count) => sum + count, 0);
  if (totalVertexCount > GEOMETRY_VERTEX_COUNT_THRESHOLD) {
    return true;
  }
  const totalLabelCount = features.reduce((count, feature) => feature.get(FeatureProperty.IS_LABELED) ? count + 1 : count, 0);
  if (totalLabelCount > LABEL_COUNT_THRESHOLD) {
    return true;
  }
  const hasProblematicStyles = features.some(feature => isStyleProblematicForVectorLayer(feature.getStyle()));
  return hasProblematicStyles;
}

function coordinateCount (geometry) {
  return geometry.getFlatCoordinates().length / geometry.getStride();
}

function isStyleProblematicForVectorLayer (style) {
  if (typeof style === 'function') {
    return false;
  }
  const styles = Array.isArray(style) ? style : [ style ];
  return styles.some(s => s.getStroke()?.getLineDash()); // Line dashes are causing an unpleasant visual effect during zoom animation as the dash pattern starting point is constantly changing.
}
